// ============================================================
// Everything the tactical map knows, for one render
// ============================================================
// BattlefieldPanel's hooks, in the order they have always run. THE ORDER
// IS LOAD-BEARING for the effects among them: React runs effects in call
// order, and the auto-placement effect, the mode report and the Esc
// listeners were written against this sequence. Pure derivations that only
// one piece of furniture reads live in that furniture instead.
//
// Several refs exist only to dodge a temporal dead zone: the marker drag
// is created first and needs things (the camera setter, the terrain rules,
// the climb report) that are built after it, so it reads them through refs
// that are filled in during the render.

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RailsOpenContext } from '../shell/ShellColumns';
import useBattlefieldCamera from '../../hooks/useBattlefieldCamera';
import useTokenDrag from '../../hooks/useTokenDrag';
import useHeadingDrag from '../../hooks/useHeadingDrag';
import useBattlefieldMeasure from '../../hooks/useBattlefieldMeasure';
import useBattlefieldBuild from '../../hooks/useBattlefieldBuild';
import useDeployment from '../../hooks/useDeployment';
import usePingGesture, { SLOP_PX } from '../../hooks/usePingGesture';
import useDisplayCamera from '../../hooks/useDisplayCamera';
import useDisplayWalks from '../../hooks/useDisplayWalks';
import useBeatsOnMap from './useBeatsOnMap';
import { placeWalking } from '../../engine/display/walk';
import useHoldsStill from '../../hooks/useHoldsStill';
import useCoverCue from './useCoverCue';
import { crawlBudget } from '../../engine/cover/pinnedAsk';
import { indexReach, tileAt } from '../../engine/battlefield/movement';
import useDisplayAction from './useDisplayAction';

const EMPTY_TOKENS = {};
import { useDisplayPrefs, useMapPrefs } from '../../hooks/useLogOverlay';
import { DISPLAY_FOV } from '../../engine/display/camera';
import { lensFor } from '../../engine/display/lens';
import { MIN_PITCH } from '../../engine/battlefield/projection';
import useMapSolids from '../../hooks/useMapSolids';
import { verticalStop } from '../../engine/battlefield/reconcile';
import { terrainRulesFor } from '../../engine/battlefield/traitRules';
import { DEFAULT_BOUNDARY, EMPTY } from './panelDefaults';
import { usePanelModes, modeFlags, useReportColumn } from './usePanelModes';
import { useAutoPlacedTokens } from './useAutoPlacedTokens';
import { useMovementRules, useMovingSelection, useLevels, useReach, useClimbNotes } from './useMovement';
import { dragRouteFor, reportMove } from './movement';
import { tileOf, groundAtFor, groundPointOn, lookFrom, deployArrivalsAt, onWindowKey, isTyping } from './panelGeometry';
import { centreMarker } from '../../engine/battlefield/deployment';

/** The arena, its solids, the camera, and the ground under a point. */
function usePanelGeometry({ terrain, renderer, roster, tokens, hiddenIds }) {
  // Parametric solids and meshed cubes, as one list - the only language
  // the renderer, line of sight and movement have ever spoken. With them, every Object that counts as terrain.
  const boundary = useMemo(() => terrain?.boundary || DEFAULT_BOUNDARY, [terrain]);
  const { solids, tooBig } = useMapSolids(terrain, roster, tokens, hiddenIds);
  // The hand camera eases on the display (T5), and not on a screen that holds still; the classic map snaps.
  const still = useHoldsStill();
  const display = renderer === 'display';
  // On the display a right press waits out the ping's slop before it orbits (the follow, 2026-10-21).
  const cam = useBattlefieldCamera(boundary, { eased: display && !still, orbitSlop: display ? SLOP_PX : 0 });
  const groundAt = useMemo(() => groundAtFor(terrain, boundary), [terrain, boundary]);
  // No camera here: a camera frame must not re-render the panel. The scene subscribes (PanelMapColumn
  // `useSceneMap`); gestures ask `getCamera()` when they happen.
  const { getCamera, setCamera, cameraStore } = cam;
  const groundPointFor = useCallback((e) => groundPointOn(e, getCamera(), boundary), [getCamera, boundary]);
  return {
    ...{ boundary, solids, tooBig, cam, getCamera, setCamera, cameraStore },
    ...{ groundAt, groundPointFor, tile: tileOf(boundary) },
  };
}

/** The gesture refs: what a pointer handler must see that it set a moment ago. */
function usePanelRefs() {
  return {
    // The marker drag's route finder, set once `walkFor` exists.
    tokenDrag: useRef(null),
    // The <svg> itself, stashed from the last press: the sweep asks it for
    // every marker's foot.
    sceneEl: useRef(null),
    // Where the right button went down, and what was under it (the ping).
    pressPoint: useRef(null),
    // A structure being dragged about, while it is being dragged about.
    structureDrag: useRef(null),
    // Where the LEFT button went down, to tell a click from a measurement.
    leftPress: useRef(null),
    lookFromRef: useRef(null),
    stopVerticalRef: useRef((x, z, fromY, toY) => toY),
    reportClimbRef: useRef(null),
    // May a carried marker be put down here? The Pinned crawl refuses a drop outside its reach (2026-10-25).
    mayPutDownRef: useRef(null),
    rulesForRef: useRef(() => terrainRulesFor([])),
  };
}

/** Carrying a marker: its route, its floors and ceilings, its stride and its climbs; a ghost on the display. */
function useMarkerDrag(panel, geo, refs, { ghost, onSelectTarget }) {
  const { tokenDrag, lookFromRef, stopVerticalRef, rulesForRef, reportClimbRef } = refs;
  const drag = useTokenDrag({
    getCamera: geo.getCamera,
    boundary: geo.boundary,
    tokens: panel.tokens,
    // What a marker STANDS ON, so a move walks it up onto a crate and down
    // into a trench without anyone dragging it vertically.
    solids: geo.solids,
    groundAt: geo.groundAt,
    // Flood once per drag; the hook asks this for a stopping point.
    routeFor: (token) => tokenDrag.current?.(token),
    // One click on a marker targets them (v8.319.0). Acting as somebody is
    // the order rail's and the gallery's job now.
    onSelectTarget,
    onLookFrom: useCallback((id) => lookFromRef.current?.(id), [lookFromRef]),
    // Floors and ceilings, for the wheel. Phase and Incorporeal pass
    // through; a flyer does not - flying is not phasing.
    stopVertical: useCallback((x, z, fromY, toY) => stopVerticalRef.current(x, z, fromY, toY), [stopVerticalRef]),
    // Their own stride, so a Dreadnought steps over what stops a guardsman.
    maxStep: (token) => rulesForRef.current(token?.id).maxStep,
    // A CLIMB IS WORTH WRITING DOWN.
    onMoved: useCallback((id, storeys) => reportClimbRef.current?.(id, storeys), [reportClimbRef]),
    mayPutDown: useCallback((token) => refs.mayPutDownRef.current?.(token) ?? true, [refs.mayPutDownRef]),
    writeToken: panel.writeToken,
    // THE GHOST (plan design-gaps, M2): on the display a drag carries a ghost and the release commits; the
    // classic map keeps its live drag.
    ghost,
  });
  stopVerticalRef.current = (x, z, fromY, toY) =>
    panel.ignoreTerrain ? toY : verticalStop(x, z, fromY, toY, geo.solids, { floor: geo.groundAt(x, z) });
  return drag;
}

/**
 * Deploy's gesture state: the formation drag (a ref for the handler, state
 * for the preview), the name dragged over a marker, the armed REPLACE, the
 * right-drag sweep (a box in CLIENT coordinates, which is what the pointer
 * and the browser's hit test on each marker's foot both speak), and the
 * line-or-block setting - a setting because Alt is not ours to rely on
 * (v8.30.0).
 */
function useDeployGestureState({ waitingRooms, activeRoomId }) {
  const arrivalPlan = useRef(null);
  const formation = useRef(null);
  const [formationLine, setFormationLine] = useState(null);
  const [dropOverId, setDropOverId] = useState(null);
  const [replaceArmed, setReplaceArmed] = useState(false);
  const sweep = useRef(null);
  const [sweepBoxState, setSweepBoxState] = useState(null);
  const [formationShape, setFormationShape] = useState('line');
  const activeRoom = useMemo(
    () => waitingRooms.find((r) => r.id === activeRoomId) ?? null,
    [waitingRooms, activeRoomId],
  );
  return {
    arrivalPlan,
    formation,
    formationLine,
    setFormationLine,
    dropOverId,
    setDropOverId,
    replaceArmed,
    setReplaceArmed,
    sweep,
    sweepBoxState,
    setSweepBoxState,
    formationShape,
    setFormationShape,
    activeRoom,
  };
}

/** The deploy marker, Deploy's gestures, and new arrivals stood at the marker. */
function useDeploy(panel, geo) {
  const { deployment, tokens, writeToken } = panel;
  // Which point a click in Deploy plants: null the main marker, else a team's own (plan scene-editor-library, B).
  const [plantFor, setPlantFor] = useState(null);
  const deployment_ = useDeployment({
    marker: deployment,
    setMarker: panel.onSetDeployment,
    plantFor,
    arrivals: panel.arrivals,
    setArrivals: panel.onSetArrivals,
    getCamera: geo.getCamera,
    boundary: geo.boundary,
    solids: geo.solids,
    // Hidden terrain is the GM's: a granted player's plant never lands on it.
    isGm: panel.isGm,
    tokens,
    writeToken,
    avoid: panel.avoidOnDeploy,
    groundAt: geo.groundAt,
  });
  const gestures = useDeployGestureState(panel);
  useArrivalPlacing(panel, geo, deployment_, gestures.arrivalPlan);
  return { deployment_, ...gestures, plantFor, setPlantFor };
}

/** No team arrival points: on a stage that stands its cast round the centre. */
const NO_POINTS = Object.freeze({});

/**
 * Newcomers on the roster stood at the marker - or at their team's own arrival point (plan scene-editor-library, B).
 * A stage whose roster is the scene's own cast (`castAtCentre`, the Scene Editor) stands them round the arena's centre
 * instead: the marker is the travellers' door, and a cut puts unmarked cast there too (2026-10-28).
 */
function useArrivalPlacing(panel, geo, deployment_, arrivalPlan) {
  const { onStampTeam, roster, tokens, writeToken } = panel;
  const centre = useMemo(
    () => (panel.castAtCentre ? centreMarker(geo.boundary) : null),
    [panel.castAtCentre, geo.boundary],
  );
  const deployment = centre ?? panel.deployment;
  const teamPoints = centre ? NO_POINTS : panel.arrivals;
  const deployArrivals = useCallback(
    (arrivals) =>
      deployArrivalsAt(arrivals, { deployment, deployment_, arrivalPlan, tile: geo.tile, onStampTeam, teamPoints }),
    [deployment, deployment_, arrivalPlan, geo.tile, onStampTeam, teamPoints],
  );
  useAutoPlacedTokens({ roster, tokens, writeToken, boundary: geo.boundary, deployArrivals });
}

/**
 * A ping is speech, and not allowed while building - right-drag already
 * means erase there - NOR IN DEPLOY (v8.24.0), which owns the right button
 * for recall and the sweep. It lands where the press went down.
 */
function usePanelPings(panel, cancelDrag, pressPoint, { building, deploying }) {
  const { onPing } = panel;
  const pingGesture = usePingGesture({
    enabled: !!onPing && !building && !deploying && !panel.isLocked('ping'),
    onCancelDrag: cancelDrag,
    onPing: useCallback(
      ({ type }) => {
        const press = pressPoint.current;
        if (press?.at) onPing?.({ type, x: press.at.x, z: press.at.z, y: press.y, describeAt: press.describeAt });
      },
      [onPing, pressPoint],
    ),
  });
  return { pingGesture };
}

/**
 * Where the camera is sent: from a marker's eye, or to wherever somebody
 * pointed. Keyed on the counter, not on the point: clicking the same log
 * entry twice must bring the camera back. Only the TARGET moves - the angle
 * and the zoom are this viewer's own.
 */
function useCameraAim({ tokens, focusOn }, setCamera, lookFromRef) {
  const lookFromToken = useCallback(
    (tokenId) => {
      const token = tokens[tokenId];
      if (token) setCamera((c) => lookFrom(c, token, MIN_PITCH));
    },
    [tokens, setCamera],
  );
  lookFromRef.current = lookFromToken;
  useEffect(() => {
    if (!focusOn) return;
    setCamera((c) => ({ ...c, target: { x: focusOn.x, z: focusOn.z } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOn?.n]);
}

/** The tape measure over the markers on the map, and whether its reading was applied. */
function useTape(panel, geo, tokenList, autoTarget) {
  const { line, clear, pending, measureHandlers } = useBattlefieldMeasure({
    getCamera: geo.getCamera,
    tokens: tokenList,
    // Turning terrain off map-wide is the same thing as measuring across an
    // empty field, so it is one substitution rather than a flag threaded
    // through the whole measurement.
    solids: panel.ignoreTerrain ? EMPTY : geo.solids,
    weaponRange: panel.weaponRange,
    isGm: panel.isGm,
    relationRules: panel.relationRules,
    // The far end of the tape is nearly always the person you are about to
    // shoot. The map already knew; now the rest of the app does.
    onMeasuredTo: autoTarget ? panel.onSelectTarget : undefined,
  });
  const [applied, setApplied] = useState(false);
  return { line, clearLine: clear, measurePending: pending, measureHandlers, applied, setApplied };
}

/** The markers on the map, who they are, the tape measure, and the movement band shown. */
function usePanelMeasure(panel, geo, autoTarget) {
  const { roster, tokens } = panel;
  const names = useMemo(() => Object.fromEntries(roster.map((c) => [c.id, c.name])), [roster]);
  // Only combatants still on the roster: a token whose combatant left is a
  // ghost nobody can select.
  const tokenList = useMemo(() => Object.values(tokens).filter((t) => names[t.id] !== undefined), [tokens, names]);
  const tape = useTape(panel, geo, tokenList, autoTarget);
  // Auto by default: every band at once, so "how far in one action" is
  // answered without choosing an action first.
  const [movementMode, setMovementMode] = useState('auto');
  return { names, tokenList, ...tape, movementMode, setMovementMode };
}

/**
 * The map's keys. Esc dismisses a measurement - the line deliberately
 * outlives the drag that made it, so something has to take it away again -
 * and is never prevented: Esc belongs to the rest of the app too.
 */
function useEscapeClearsLine(line, clearLine) {
  useEffect(() => {
    if (!line) return undefined;
    return onWindowKey((e) => {
      if (e.key === 'Escape') clearLine();
    });
  }, [line, clearLine]);
}

/**
 * Drawing an arena ends with Esc (cancel) or with leaving Build - a
 * half-drawn ring must never survive into Play mode's click grammar.
 */
function useArenaDraftEnds(build, building) {
  useEffect(() => {
    if (!build.arenaDraft) return undefined;
    if (!building) {
      build.cancelArenaDraw();
      return undefined;
    }
    return onWindowKey((e) => {
      if (e.key === 'Escape') build.cancelArenaDraw();
    });
  }, [build.arenaDraft, building, build]);
}

/**
 * Esc ends what Build has in hand (plan map-display, F; the user's Build ruling): a drag in progress is
 * thrown away unbuilt, else a selected structure is let go. Never prevented, like the line's.
 */
function useEscapeEndsBuild(build, building) {
  const holding = building && (!!build.preview || !!build.selected);
  useEffect(() => {
    if (!holding) return undefined;
    return onWindowKey((e) => {
      if (e.key !== 'Escape') return;
      if (build.preview) {
        build.cancelStroke();
        return;
      }
      // Backing out of a field or closing a modal is not letting go of the structure. Only a MODAL: the rails'
      // panels are non-modal dialogs that handle their own Esc.
      if (e.defaultPrevented || isTyping() || document.querySelector('[aria-modal="true"]')) return;
      build.setSelected(null);
    });
  }, [holding, build]);
}

/**
 * Delete takes the selected structure away - only while the Structures
 * tool is up, and never while anything is being typed into.
 */
function useDeleteStructure(build, shaping) {
  useEffect(() => {
    if (!shaping || !build.selected) return undefined;
    return onWindowKey((e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (isTyping()) return;
      e.preventDefault();
      build.deleteStructure();
    });
  }, [shaping, build]);
}

/**
 * THE PINNED CRAWL (2026-10-25): while the answering screen crawls a figure to cover, he is the one moving, and his
 * whole movement is half his Half Move - the reach, the route and the grading follow from that one substitution.
 */
function useCrawlingSelection(selection, crawl) {
  const base = selection.movingMovement;
  // Memoised on his own movement: a fresh object each render missed every reach memo below it, re-flooding and
  // re-grading the reach on every pointer move and restarting its ripple (the review).
  const crawlMovement = useMemo(() => {
    const metres = crawlBudget(base);
    return { ...base, half: metres, full: metres, charge: metres, run: metres };
  }, [base]);
  if (!crawl || crawl.combatantId !== selection.movingId) return selection;
  return { ...selection, movingMovement: crawlMovement, crawling: selection.movingId };
}

/** Is the token's tile inside the reach? */
const within = (reach, token, tileSize) => !!tileAt(indexReach(reach, tileSize), token, tileSize);

/**
 * Who is moving, how they cross the map, and what the map shows about it:
 * the reach, the route, the storeys, the climb. Also where the marker drag
 * finds its route and where a finished move is reported, through the refs
 * the drag was created with.
 */
function usePanelMovement(panel, geo, drag, refs, measure) {
  // Whoever is being carried is the mover while they are (2026-11-04); the selection otherwise.
  const selection = useCrawlingSelection(
    useMovingSelection({ ...panel, carriedId: drag.dragOrigin?.tokenId }),
    panel.crawl,
  );
  const rules = useMovementRules({ ...panel, solids: geo.solids, groundAt: geo.groundAt }, refs.rulesForRef);
  // The ghost stands in for its marker (plan design-gaps, M2): the route, its bands and its label follow the carry.
  const tokens = drag.carried ? { ...panel.tokens, [drag.carried.id]: drag.carried } : panel.tokens;
  const movementMode = selection.crawling ? 'half' : measure.movementMode;
  const m = { ...panel, ...geo, ...selection, ...rules, tokens, movementMode };
  const levels = useLevels({ ...m, reportClimbRef: refs.reportClimbRef });
  const reach = useReach({ ...m, dragOrigin: drag.dragOrigin });
  const climb = useClimbNotes({ ...m, vector: reach.vector });
  refs.tokenDrag.current = (token) => dragRouteFor(token, m);
  // A finished move: its line (the route the map drew for it), the kill-zone check, a climb's note.
  refs.reportClimbRef.current = (id, storeys) =>
    reportMove(id, storeys, { ...m, names: measure.names, vector: reach.vector });
  refs.mayPutDownRef.current = (token) =>
    !selection.crawling || token.id !== selection.crawling || within(reach.reach, token, geo.boundary.tileSize);
  return { ...selection, ...rules, ...levels, ...reach, ...climb };
}

/** The map's keys, in the order they have always been registered. */
function usePanelKeys({ line, clearLine }, { build, building, shaping }) {
  useEscapeClearsLine(line, clearLine);
  useArenaDraftEnds(build, building);
  useEscapeEndsBuild(build, building);
  useDeleteStructure(build, shaping);
}

/**
 * A click on a marker targets them and - the viewer's setting, on by default (2026-11-04) - opens the target's
 * panel; a click on bare ground closes the panels (sceneGestures). Outside a session there are no panels to open.
 */
function useClickTarget(onSelectTarget, opensPanel) {
  const openRail = useContext(RailsOpenContext);
  return useCallback(
    (id) => {
      onSelectTarget?.(id);
      if (opensPanel) openRail?.('target');
    },
    [onSelectTarget, opensPanel, openRail],
  );
}

/**
 * The mode, the viewer's map preferences, the heading drag and the build
 * tools - the hooks that hang off which mode we are in.
 */
function useModeTools(panel, geo) {
  const modes = usePanelModes(panel);
  const prefs = useMapPrefs();
  const flags = modeFlags(modes.mode, modes.buildOption);
  const { getCamera } = geo;
  const heading = useHeadingDrag({
    getCamera,
    tokens: panel.tokens,
    writeToken: panel.writeToken,
    enabled: flags.canTurn,
  });
  useReportColumn(flags.wantsTheColumn, panel.onBuildingChange);
  const terrain = panel.terrain || { boundary: geo.boundary, solids: EMPTY };
  const build = useBattlefieldBuild({ getCamera, terrain, setTerrain: panel.setTerrain });
  // The Structures tool has its own gestures; the cube machinery stands
  // down while it is up. View is not a tool at all.
  const tooling = flags.building && !flags.looking;
  const shaping = tooling && build.tool === 'structure';
  // WHICH RENDERER, decided once (plan map-display): the panel's own - the display in a session (v12.0.0), the
  // classic map in the Assets editors. Every layer reads `map.renderer`.
  return {
    ...modes,
    ...flags,
    ...heading,
    prefs,
    renderer: panel.renderer,
    build,
    shaping,
    layingCubes: tooling && !shaping,
  };
}

/**
 * The tactical map's whole live state for one render, flat: the panel's
 * furniture and its gesture handlers read what they need from it.
 */
export function useBattlefieldPanel(panel) {
  const geo = usePanelGeometry(panel);
  const refs = usePanelRefs();
  const tools = useModeTools(panel, geo);
  const onSelectTarget = useClickTarget(panel.onSelectTarget, tools.prefs.openTargetOnClick);
  const drag = useMarkerDrag(panel, geo, refs, { ghost: tools.renderer === 'display', onSelectTarget });
  const deploy = useDeploy(panel, geo);
  const [hoverCell, setHoverCell] = useState(null);
  const pings = usePanelPings(panel, geo.cam.cancelDrag, refs.pressPoint, tools);
  useCameraAim(panel, geo.setCamera, refs.lookFromRef);
  const measure = usePanelMeasure(panel, geo, tools.prefs.autoTarget);
  usePanelKeys(measure, tools);
  const movement = usePanelMovement(panel, geo, drag, refs, measure);
  // The attack reads this at the moment of firing. See `measureLineRef`.
  if (panel.measureLineRef) panel.measureLineRef.current = measure.line;
  const follow = usePanelFollow(panel, geo, tools);
  const walked = useDrawnTokens(panel.tokens ?? EMPTY_TOKENS, measure.tokenList, follow.walkPositions);
  const onMap = useBeatsOnMap(panel, tools.renderer === 'display', walked.drawnTokens);
  const drawn = useDrawnTokens(walked.drawnTokens, walked.drawnTokenList, onMap.shoves, shoveToken);
  const display = useDisplayAction(displayArgs(panel, { tools, follow, measure, deploy, drawn }));
  // The take-cover cue (2026-10-25): who wears cover screens and what each gun reads, in the world (the scene
  // projects the screens for its own camera: PanelMapColumn `useSceneMap`).
  const cover = useCoverCue(panel, geo, tools, movement, drag);
  return Object.assign(
    {
      hoverCell,
      cover,
      setHoverCell,
      display,
      ...drawn,
      beats: onMap.beats,
      looks: onMap.looks,
      shoves: onMap.shoves,
      // A heavy landing's kick (K3), as a ground vector: the scene turns it into a shift for its camera.
      kick: onMap.kick,
    },
    geo,
    refs,
    drag,
    tools,
    deploy,
    pings,
    measure,
    movement,
    follow,
  );
}

/**
 * THE DRAWN TOKENS (plan design-gaps, M3; the Track M review): the figures mid-walk stand where the walk has them
 * this frame, placed ONCE here for every drawing layer - markers, beats, threads, wedges, occlusion, horde areas,
 * the firing line - while `panel.tokens` stays the committed record the rules read (reach, routes, the drag). The
 * same objects when nobody walks, so nothing downstream redraws; the classic map never has walkers.
 */
function useDrawnTokens(tokens, tokenList, moved, place = placeWalking) {
  return useMemo(() => {
    if (!Object.keys(moved).length) return { drawnTokens: tokens, drawnTokenList: tokenList };
    const drawnTokenList = tokenList.map((t) => (moved[t.id] ? place(t, moved[t.id]) : t));
    const drawnTokens = { ...tokens };
    for (const t of drawnTokenList) if (moved[t.id]) drawnTokens[t.id] = t;
    return { drawnTokens, drawnTokenList };
  }, [tokens, tokenList, moved, place]);
}

/** A figure shoved by a beat (K3): its drawn spot moved along the ground, its height kept. */
const shoveToken = (t, s) => ({ ...t, x: t.x + s.dx, z: t.z + s.dz });

/**
 * The display's camera follows the action (plan map-display, C): it hands
 * back the panel's camera with gestures that take the wheel, and the Follow
 * that gives it back - as the viewer's display options say (theirs, else the
 * table's). On the classic map the camera is the hand's alone, and flat.
 */
function usePanelFollow(panel, geo, tools) {
  const holdsStill = useHoldsStill();
  const display = useDisplayPrefs(panel.tableDefaults);
  const shown = tools.renderer === 'display';
  const lens = shown && display.perspective ? DISPLAY_FOV : undefined;
  // The figures walking now (plan design-gaps, M3): the display's alone, and none on a screen that holds still.
  const walks = useDisplayWalks(shown ? panel.walks : undefined, holdsStill);
  const followed = useDisplayCamera(
    followArgs(panel, geo, { shown, display, holdsStill, lens, walking: walks.walking }),
  );
  const { cam, following, wheelTaken, follow, pair, shot } = followed;
  useDisplayLens(geo, shown, lens, following && !!pair);
  useArrowTurns(shown ? cam.onArrow : null);
  // Who the action is about on this screen: the pair the camera framed, else this viewer's own.
  const actionPair = pair ?? { actorId: panel.actorId ?? null, targetId: panel.targetId ?? null };
  return {
    cam,
    wheelTaken,
    follow,
    // The camera label's words (Z6): the shot this screen took.
    cameraLabel: shot,
    displayPrefs: display,
    actionPair,
    walkPositions: walks.positions,
    walksPlaying: walks.playing,
  };
}

/** What the display's action reads (useDisplayAction): the renderer, the options, the pair, the figures, the hover. */
function displayArgs(panel, { tools, follow, measure, deploy, drawn }) {
  return {
    shown: tools.renderer === 'display',
    prefs: follow.displayPrefs,
    pair: follow.actionPair,
    tokens: panel.tokens ?? EMPTY_TOKENS,
    tokenList: measure.tokenList,
    hovered: deploy.dropOverId ?? panel.hoveredId ?? null,
    drawnTokens: drawn.drawnTokens,
  };
}

/** What the follow camera reads (useDisplayCamera): the panel's pair and turn, the viewer's options, the map. */
function followArgs(panel, geo, { shown, display, holdsStill, lens, walking }) {
  return {
    enabled: shown,
    follow: display.follow,
    cut: panel.cameraCut ?? null,
    cam: geo.cam,
    tokens: panel.tokens,
    actorId: panel.actorId ?? null,
    targetId: panel.targetId ?? null,
    turnActorId: panel.turnActorId ?? null,
    isGm: !!panel.isGm,
    // The attacks this screen has heard (the camera's beat): live ones, never the last turn's replay.
    impacts: panel.liveImpacts,
    holdsStill,
    shoulder: display.shoulder,
    breathing: display.breathing,
    lens,
    xray: display.xray,
    solids: geo.solids,
    walking,
  };
}

/**
 * The arrow keys turn and tilt the display's camera (T5) - with the pointer over the map, and not while typing or
 * with a modifier held, so they never take a key meant for anything else on the page.
 */
function useArrowTurns(onArrow) {
  // The latest handler in a ref: while following it is made anew every render, and the listener need not be.
  const latest = useRef(onArrow);
  latest.current = onArrow;
  const on = !!onArrow;
  useEffect(() => {
    if (!on) return undefined;
    return onWindowKey((e) => {
      if (!arrowForTheMap(e)) return;
      e.preventDefault();
      latest.current?.(e.key);
    });
  }, [on]);
}

/**
 * The parts of widgets that step with the arrow keys themselves: with one focused, its arrows are its own. The focused
 * element's OWN role, not an ancestor's - a plain button that sits in a tab row (the log's toggle) steps nothing.
 */
const ARROW_WIDGETS =
  '[role="tab"],[role="option"],[role="radio"],[role="slider"],[role="separator"],[role="menuitem"],[role="gridcell"]';

/**
 * An arrow key the map may take: unmodified, nobody's already (the order rail stepping by it), the caret in no field
 * and the focus on no widget of its own - and the pointer over the display.
 */
function arrowForTheMap(e) {
  if (!e.key.startsWith('Arrow') || e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey)
    return false;
  if (isTyping() || document.activeElement?.matches?.(ARROW_WIDGETS)) return false;
  return !!document.querySelector('svg[data-renderer="display"]:hover');
}

/**
 * The panel's own camera wears the right lens (`lensFor`) whoever set it last,
 * so every gesture and pick sees what is drawn - and the classic map never
 * keeps a lens or a lift a shot left behind. Asked of the store each time the
 * camera changes (`lensFor` of a camera already wearing it is that camera, so
 * the set it makes is heard once and settles).
 */
function useDisplayLens({ cameraStore }, display, lens, shotOwnsIt) {
  useEffect(() => {
    const wear = () => {
      const camera = cameraStore.get();
      const worn = lensFor(camera, display, lens, shotOwnsIt);
      if (worn !== camera) cameraStore.set(worn);
    };
    wear();
    return cameraStore.subscribe(wear);
  }, [cameraStore, display, lens, shotOwnsIt]);
}
